import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  IconButton,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
} from "@mui/material";
import {
  Save as SaveIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Info as InfoIcon,
  Archive as ArchiveIcon,
  BarChart as BarChartIcon,
  Place as MapIcon,
  Home as HomeIcon,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { useAppDispatch } from "@hooks/redux";
import { createApiary } from "@store/slices/locationsSlice";
import NominatimAutocomplete from "@components/common/NominatimAutocomplete";
import ColorPicker from "@components/common/ColorPicker";
import HiveAmountPreview from "@components/apiaries/HiveAmountPreview";
import SimpleHiveIcon from "@components/hives/SimpleHiveIcon";
import NumberInput from "@components/common/NumberInput";
import SaveToolbar from "@components/common/SaveToolbar";
import UnsavedChangesDialog from "@components/common/UnsavedChangesDialog";
import { useUnsavedChanges } from "@hooks/useUnsavedChanges";
import { apiClient } from "@api/index";
import { KENYA_LOCATIONS, County, Constituency } from "@constants/kenyaLocations";

// TabPanel component for tab content
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`apiary-tabpanel-${index}`}
      aria-labelledby={`apiary-tab-${index}`}
      style={{ width: '100%' }}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>{children}</Box>}
    </div>
  );
}

interface HiveType {
  id: number;
  name: string;
  type: string;
  description: string;
}

interface ApiaryFormData {
  name: string;
  description: string;
  address: string;
  country_code: string;
  city: string;
  postal_code: string;
  street: string;
  street_no: string;
  latitude: number | undefined;
  longitude: number | undefined;
  hex_color: string;
  roofed: boolean;
  hive_amount: number;
  hive_prefix: string;
  hive_offset: number;
  hive_type: number | null;
  bee_race: string;
}

const ApiaryCreate: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [activeTab, setActiveTab] = useState(0);
  const [showLoadingIcon, setShowLoadingIcon] = useState(false);
  const [error, setError] = useState("");
  const [isEdited, setIsEdited] = useState(false);

  const [hiveTypes, setHiveTypes] = useState<HiveType[]>([]);
  const [loadingHiveTypes, setLoadingHiveTypes] = useState(true);
  const [beeRaces, setBeeRaces] = useState<{ value: string; label: string }[]>([]);
  const [loadingBeeRaces, setLoadingBeeRaces] = useState(true);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<ApiaryFormData>({
    defaultValues: {
      name: "",
      description: "",
      address: "",
      country_code: "KE",
      city: "",
      postal_code: "",
      street: "",
      street_no: "",
      latitude: undefined,
      longitude: undefined,
      hex_color: "#F8B133",
      roofed: false,
      hive_amount: 1,
      hive_prefix: "Hive ",
      hive_offset: 1,
      hive_type: 1,
      bee_race: "",
    },
  });

  // Location State
  const [selectedCounty, setSelectedCounty] = useState<County | null>(null);
  const [selectedConstituency, setSelectedConstituency] = useState<Constituency | null>(null);
  const [selectedWard, setSelectedWard] = useState<string | null>(null);
  const [village, setVillage] = useState('');
  const [landmark, setLandmark] = useState('');

  // Update form values when location parts change
  useEffect(() => {
    if (selectedCounty) {
      setValue('city', selectedCounty.name);
      setValue('country_code', 'KE');
    }
  }, [selectedCounty, setValue]);

  useEffect(() => {
    const parts = [];
    if (village) parts.push(village);
    if (selectedWard) parts.push(selectedWard);
    if (selectedConstituency) parts.push(selectedConstituency.name);

    setValue('street', parts.join(', '));
  }, [selectedConstituency, selectedWard, village, setValue]);

  useEffect(() => {
    setValue('street_no', landmark);
  }, [landmark, setValue]);

  const handleLocationSelect = (location: {
    address: string;
    lat: number;
    lng: number;
    city?: string;
    country_code?: string;
    postal_code?: string;
    street?: string;
    street_number?: string;
  }) => {
    setValue("address", location.address);
    setValue("latitude", location.lat);
    setValue("longitude", location.lng);
    if (location.city) {
      // Try to match county
      const county = KENYA_LOCATIONS.find(c => c.name === location.city || c.name.includes(location.city || ''));
      if (county) setSelectedCounty(county);
      else setValue("city", location.city);
    }
    if (location.country_code) setValue("country_code", location.country_code);
    if (location.postal_code) setValue("postal_code", location.postal_code);

    // We don't auto-fill street/street_no from Google Maps to avoid overwriting the structured data logic
    // unless we want to parse it, but for now let's keep it simple or just set address
  };

  useEffect(() => {
    const fetchHiveTypes = async () => {
      try {
        setLoadingHiveTypes(true);
        const response = await apiClient.get("/hive-types/");
        if (response.data.success) {
          setHiveTypes(response.data.data);
        } else {
          // Fallback to default hive types if API fails
          setHiveTypes([
            { id: 1, name: "Langstroth", type: "langstroth", description: "Standard Langstroth hive" },
            { id: 2, name: "Kenya Top Bar Hive", type: "top_bar", description: "Kenya Top Bar Hive" },
            { id: 3, name: "Dadant", type: "dadant", description: "Dadant hive" },
            { id: 4, name: "Log Hive", type: "log", description: "Traditional log hive" },
          ]);
        }
      } catch (error) {
        console.error("Failed to fetch hive types:", error);
        setHiveTypes([
          { id: 1, name: "Langstroth", type: "langstroth", description: "Standard Langstroth hive" },
          { id: 2, name: "Kenya Top Bar Hive", type: "top_bar", description: "Kenya Top Bar Hive" },
          { id: 3, name: "Dadant", type: "dadant", description: "Dadant hive" },
          { id: 4, name: "Log Hive", type: "log", description: "Traditional log hive" },
        ]);
      } finally {
        setLoadingHiveTypes(false);
      }
    };

    fetchHiveTypes();
  }, []);

  useEffect(() => {
    const fetchBeeRaces = async () => {
      try {
        setLoadingBeeRaces(true);
        const response = await apiClient.get("/hives/bee_races/");
        if (response.data.success) {
          setBeeRaces(response.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch bee races:", error);
        setBeeRaces([
          { value: "african", label: "African (Apis mellifera scutellata)" },
          { value: "italian", label: "Italian (Apis mellifera ligustica)" },
          { value: "carnica", label: "Carnica (Apis mellifera carnica)" },
          { value: "buckfast", label: "Buckfast" },
        ]);
      } finally {
        setLoadingBeeRaces(false);
      }
    };

    fetchBeeRaces();
  }, []);

  // Track form changes
  React.useEffect(() => {
    if (isDirty) {
      setIsEdited(true);
    }
  }, [isDirty]);

  // Navigation guard
  const { showDialog, confirmNavigation, cancelNavigation } = useUnsavedChanges(
    {
      when: isEdited && !showLoadingIcon,
      message: "You have unsaved changes. Are you sure you want to leave?",
    },
  );

  const formValues = watch();

  const onSubmit = async (data: ApiaryFormData) => {
    try {
      setShowLoadingIcon(true);
      setError("");

      const apiaryData = {
        name: data.name,
        description: data.description,
        address: data.address,
        country_code: data.country_code,
        city: data.city,
        postal_code: data.postal_code,
        street: data.street,
        street_no: data.street_no,
        latitude: data.latitude || undefined,
        longitude: data.longitude || undefined,
        hex_color: data.hex_color,
        roofed: data.roofed,
        hive_amount: data.hive_amount,
        prefix: data.hive_prefix,
        offset: data.hive_offset,
        hive_type: data.hive_type,
        bee_race: data.bee_race,
      };

      await dispatch(createApiary(apiaryData));
      setIsEdited(false);
      navigate("/");
    } catch (err: any) {
      setError(err.message || "Failed to create apiary");
    } finally {
      setShowLoadingIcon(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleNextTab = () => {
    if (activeTab < 4) {
      setActiveTab(activeTab + 1);
    }
  };

  const handlePreviousTab = () => {
    if (activeTab > 0) {
      setActiveTab(activeTab - 1);
    }
  };



  const handleColorChange = (color: string) => {
    setValue("hex_color", color, { shouldDirty: true });
  };

  const isValid = !errors.name && !!formValues.name;

  const tabs = [
    {
      title: "New Apiary",
      title_mobile: "",
      icon: <InfoIcon />,
    },
    {
      title: "1. Location",
      title_mobile: "1",
      icon: <HomeIcon />,
    },
    {
      title: "2. Place",
      title_mobile: "2",
      icon: <MapIcon />,
    },
    {
      title: "3. Configuration",
      title_mobile: "3",
      icon: <ArchiveIcon />,
    },
    {
      title: "4. Hive Amount",
      title_mobile: "4",
      icon: <BarChartIcon />,
    },
  ];

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ width: '100%' }}>
        {/* Tabs Header */}
        <Box sx={{ width: '100%', bgcolor: '#F8B133' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant={isMobile ? "scrollable" : "fullWidth"}
            scrollButtons="auto"
            sx={{
              minHeight: 48,
              "& .MuiTabs-indicator": {
                backgroundColor: "black",
                height: 4,
              },
              "& .MuiTab-root": {
                color: "rgba(0,0,0,0.6)",
                fontWeight: 600,
                fontSize: '0.85rem',
                textTransform: 'none',
                minHeight: 48,
                py: 1,
                "&.Mui-selected": {
                  color: "black",
                  fontWeight: 700,
                },
              },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                label={
                  isMobile && tab.title_mobile ? tab.title_mobile : tab.title
                }
                icon={tab.icon}
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Navigation Bar */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <IconButton
            onClick={handlePreviousTab}
            disabled={activeTab === 0}
            sx={{
              color: '#f29100',
              opacity: activeTab === 0 ? 0.3 : 1,
              '&:hover': { bgcolor: 'rgba(242, 145, 0, 0.1)' }
            }}
          >
            <ChevronLeftIcon fontSize="large" />
          </IconButton>

          <IconButton
            onClick={handleNextTab}
            disabled={activeTab === tabs.length - 1}
            sx={{
              color: '#f29100',
              opacity: activeTab === tabs.length - 1 ? 0.3 : 1,
              '&:hover': { bgcolor: 'rgba(242, 145, 0, 0.1)' }
            }}
          >
            <ChevronRightIcon fontSize="large" />
          </IconButton>
        </Box>

        {/* Tab Content */}
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Tab 0: Introduction */}
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4 }}>
              <Typography variant="overline" sx={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: 1, mb: 2, display: 'block' }}>
                CREATE A NEW APIARY IN 4 STEPS
              </Typography>

              <Box sx={{ textAlign: "center" }}>
                <img
                  src="/img/apiary-in-field.png"
                  alt="Apiary in field"
                  style={{
                    width: "100%",
                    maxWidth: "100%",
                    height: "auto",
                    maxHeight: isMobile ? "40vh" : "60vh",
                    objectFit: "cover",
                    borderRadius: 4,
                  }}
                />
              </Box>
            </Box>
          </TabPanel>

          {/* Tab 1: Location Settings */}
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4 }}>
              <Typography variant="overline" sx={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: 1, mb: 2, display: 'block' }}>
                LOCATION DETAILS
              </Typography>

              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 3, bgcolor: 'background.paper' }}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="name"
                      control={control}
                      rules={{ required: "Apiary name is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Apiary Name *"
                          fullWidth
                          variant="outlined"
                          error={!!errors.name}
                          helperText={errors.name?.message}
                          inputProps={{ maxLength: 30 }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ mt: 2 }}>
                      <Controller
                        name="roofed"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={<Switch {...field} checked={field.value} color="primary" />}
                            label="This apiary is roofed"
                          />
                        )}
                      />
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        Check this if your apiary is covered or indoors.
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                      Apiary Color
                    </Typography>
                    <Typography variant="caption" color="text.secondary" paragraph>
                      Choose a color to identify this apiary on the map and in lists.
                    </Typography>
                    <ColorPicker
                      color={formValues.hex_color}
                      onChange={handleColorChange}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </TabPanel>

          {/* Tab 2: Place Details */}
          <TabPanel value={activeTab} index={2}>
            <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4 }}>
              <Typography variant="overline" sx={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: 1, mb: 2, display: 'block' }}>
                PLACE DETAILS
              </Typography>

              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 3, bgcolor: 'background.paper' }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                      Search Address
                    </Typography>
                    <NominatimAutocomplete
                      onLocationSelect={handleLocationSelect}
                      placeholder="Start typing to search for location..."
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" paragraph>
                      Or enter details manually:
                    </Typography>
                  </Grid>

                  {/* County */}
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={KENYA_LOCATIONS}
                      getOptionLabel={(option) => option.name}
                      value={selectedCounty}
                      onChange={(_, newValue) => {
                        setSelectedCounty(newValue);
                        setSelectedConstituency(null);
                        setSelectedWard(null);
                      }}
                      renderInput={(params) => <TextField {...params} label="County" placeholder="Select County" />}
                    />
                  </Grid>

                  {/* Constituency */}
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={selectedCounty?.constituencies || []}
                      getOptionLabel={(option) => option.name}
                      value={selectedConstituency}
                      disabled={!selectedCounty}
                      onChange={(_, newValue) => {
                        setSelectedConstituency(newValue);
                        setSelectedWard(null);
                      }}
                      renderInput={(params) => <TextField {...params} label="Constituency" placeholder="Select Constituency" />}
                    />
                  </Grid>

                  {/* Ward */}
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={selectedConstituency?.wards || []}
                      value={selectedWard}
                      disabled={!selectedConstituency}
                      onChange={(_, newValue) => setSelectedWard(newValue)}
                      renderInput={(params) => <TextField {...params} label="Ward" placeholder="Select Ward" />}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                      Specific Details
                    </Typography>
                  </Grid>

                  {/* Village / Estate */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Village / Estate"
                      fullWidth
                      value={village}
                      onChange={(e) => setVillage(e.target.value)}
                      placeholder="e.g. Kwa Njenga"
                    />
                  </Grid>

                  {/* Landmark */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Nearest Landmark"
                      fullWidth
                      value={landmark}
                      onChange={(e) => setLandmark(e.target.value)}
                      placeholder="e.g. Near Primary School"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                      GPS Coordinates
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="latitude"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ""}
                          label="Latitude"
                          type="number"
                          fullWidth
                          inputProps={{ step: "any" }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="longitude"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ""}
                          label="Longitude"
                          type="number"
                          fullWidth
                          inputProps={{ step: "any" }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </TabPanel>

          {/* Tab 3: Configuration */}
          <TabPanel value={activeTab} index={3}>
            <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4 }}>
              <Typography variant="overline" sx={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: 1, mb: 2, display: 'block' }}>
                HIVE CONFIGURATION
              </Typography>

              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 3, bgcolor: 'background.paper' }}>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Configure the default settings for hives in this apiary.
                </Typography>

                <Grid container spacing={4}>
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        p: 3,
                        border: "1px dashed",
                        borderColor: "divider",
                        borderRadius: 2,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Typography variant="subtitle2" gutterBottom>
                        Preview
                      </Typography>
                      <SimpleHiveIcon size="xl" color={formValues.hex_color} />
                      <Box sx={{ mt: 2, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ mb: 1, color: "text.secondary" }}>
                          Hive color (overrides layer colors)
                        </Typography>
                        <ColorPicker
                          color={formValues.hex_color}
                          onChange={handleColorChange}
                        />
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <FormControl fullWidth required>
                          <InputLabel>Hive Type</InputLabel>
                          <Controller
                            name="hive_type"
                            control={control}
                            rules={{ required: "Hive type is required" }}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label="Hive Type"
                                value={field.value || ""}
                              >
                                {loadingHiveTypes ? (
                                  <MenuItem value="">
                                    <CircularProgress size={20} /> Loading...
                                  </MenuItem>
                                ) : (
                                  hiveTypes.map((type) => (
                                    <MenuItem key={type.id} value={type.id}>
                                      {type.name}
                                    </MenuItem>
                                  ))
                                )}
                              </Select>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>Bee Race</InputLabel>
                          <Controller
                            name="bee_race"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label="Bee Race"
                                value={field.value || ""}
                              >
                                <MenuItem value="">
                                  <em>Not specified</em>
                                </MenuItem>
                                {loadingBeeRaces ? (
                                  <MenuItem value="">
                                    <CircularProgress size={20} /> Loading...
                                  </MenuItem>
                                ) : (
                                  beeRaces.map((race) => (
                                    <MenuItem key={race.value} value={race.value}>
                                      {race.label}
                                    </MenuItem>
                                  ))
                                )}
                              </Select>
                            )}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </TabPanel>

          {/* Tab 4: Hive Amount */}
          <TabPanel value={activeTab} index={4}>
            <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4 }}>
              <Typography variant="overline" sx={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: 1, mb: 2, display: 'block' }}>
                NUMBER OF HIVES
              </Typography>

              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 3, bgcolor: 'background.paper' }}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={5}>
                    <Box sx={{ p: 0 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                        How many hives do you want to create?
                      </Typography>

                      <Controller
                        name="hive_amount"
                        control={control}
                        render={({ field }) => (
                          <NumberInput
                            value={field.value}
                            onChange={field.onChange}
                            label="Number of Hives"
                            min={0}
                            max={50}
                            fullWidth
                          />
                        )}
                      />

                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
                        Naming Convention
                      </Typography>

                      <Controller
                        name="hive_prefix"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Hive Name Prefix"
                            fullWidth
                            size="small"
                            variant="outlined"
                            helperText="e.g. 'Hive' will create 'Hive 1', 'Hive 2'..."
                          />
                        )}
                      />

                      <Controller
                        name="hive_offset"
                        control={control}
                        render={({ field }) => (
                          <NumberInput
                            value={field.value}
                            onChange={field.onChange}
                            label="Start Numbering From"
                            min={1}
                            max={1000}
                            fullWidth
                          />
                        )}
                      />

                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={showLoadingIcon || !isValid}
                        startIcon={showLoadingIcon ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        sx={{
                          mt: 4,
                          bgcolor: '#F8B133',
                          color: 'black',
                          fontWeight: 'bold',
                          '&:hover': { bgcolor: '#f29100' },
                          py: 1.5,
                          width: '100%'
                        }}
                      >
                        {showLoadingIcon ? "Creating..." : "Create Apiary"}
                      </Button>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={7}>
                    <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                        Preview
                      </Typography>
                      <HiveAmountPreview
                        apiaryName={formValues.name || "New Apiary"}
                        apiaryColor={formValues.hex_color}
                        hiveAmount={formValues.hive_amount}
                        hivePrefix={formValues.hive_prefix}
                        hiveOffset={formValues.hive_offset}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </TabPanel>
        </form >
      </Box >

      {/* Unsaved Changes Dialog */}
      < UnsavedChangesDialog
        open={showDialog}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
      />
    </Box >
  );
};

export default ApiaryCreate;
